import React, { useEffect, useState } from 'react';
import { useI18n } from '../../contexts/I18nContext';
import { useClientAccess } from '../../hooks/useClientAccess';
import { ClientDetailModal } from './ClientDetailModal';
import { ClientWithDevices } from '../../types/client';
import { Pagination } from '../common/Pagination';
import { SortableHeader } from '../common/SortableHeader';
import { ListEmpty, ListErrorCard, ListLoadingCard } from '../common/ListStateCard';
import { hueOf } from '../users/UserAvatar';
import { clientInitials } from './clientInitials';
import { useClientsList, ClientSortField } from './clientsList/useClientsList';
import { isAgency } from '../../utils/clientTree';

interface ClientsTableProps {
  onClientSelect?: (client: ClientWithDevices) => void;
  refreshTrigger?: number;
}

/**
 * The clients, one row each: name, SIREN, fleet size, last activity. The
 * data lives in `useClientsList`; a row opens the client's dialog.
 */
export const ClientsTable: React.FC<ClientsTableProps> = ({ onClientSelect, refreshTrigger }) => {
  const { formatDate, formatNumber, t } = useI18n();
  const { isAnepManager, isClientAdmin } = useClientAccess();
  const list = useClientsList(refreshTrigger);
  const [selectedClient, setSelectedClient] = useState<ClientWithDevices | null>(null);

  // After a save the list reloads; the open dialog must show the fresh row, not the one it was opened with.
  useEffect(() => {
    if (!selectedClient) return;
    const fresh = list.pageRows.find((c) => c.id === selectedClient.id);
    if (fresh && fresh !== selectedClient) setSelectedClient(fresh);
  }, [list.pageRows, selectedClient]);

  const openClient = (client: ClientWithDevices) => {
    setSelectedClient(client);
    onClientSelect?.(client);
  };

  const sortHeader = (field: ClientSortField, label: string, numeric = false) => (
    <SortableHeader field={field} label={label} activeField={list.sort.field} direction={list.sort.direction} onSort={list.sortBy} numeric={numeric} />
  );

  if (list.loading) return <ListLoadingCard message={t('clients.loading')} />;
  if (list.error) return <ListErrorCard title={t('clients.errorLoading')} message={list.error} onRetry={list.reload} />;

  const { facts } = list;

  return (
    <div className="tpl-container clients-table-container">
      <div className="tpl-card">
        <div className="tpl-toolbar">
          <input
            type="search"
            data-testid="field-clients-search"
            placeholder={t('clients.searchPlaceholder')}
            value={list.searchTerm}
            onChange={(e) => { list.setSearchTerm(e.target.value); list.setCurrentPage(1); }}
            className="tpl-search"
            aria-label={t('clients.searchPlaceholder')}
          />
          <input
            type="search"
            data-testid="field-clients-siren"
            placeholder={t('clients.searchSirenPlaceholder')}
            value={list.sirenSearch}
            onChange={(e) => { list.setSirenSearch(e.target.value.replace(/\D/g, '')); list.setCurrentPage(1); }}
            className="tpl-search cl-siren-search mono"
            inputMode="numeric"
            maxLength={14}
            aria-label={t('clients.searchSirenPlaceholder')}
          />
          {list.canSeeInactive && (
            <label className="tpl-check" title={t('common.showInactiveHint')}>
              <input type="checkbox" checked={list.showInactive} onChange={(e) => { list.setShowInactive(e.target.checked); list.setCurrentPage(1); }} />
              {t('common.showInactive')}
            </label>
          )}
          <div className="tpl-facts" data-testid="clients-facts">
            <span><strong>{formatNumber(facts.total)}</strong> {t('clients.facts.clients')}</span>
            <span className="is-ok"><strong>{formatNumber(facts.active)}</strong> {t('clients.facts.active')}</span>
            {facts.inactive > 0 && <span className="is-warning"><strong>{formatNumber(facts.inactive)}</strong> {t('clients.facts.inactive')}</span>}
            {facts.agencies > 0 && <span><strong>{formatNumber(facts.agencies)}</strong> {t('clients.facts.agencies')}</span>}
            <span><strong>{formatNumber(facts.devices)}</strong> {t('clients.facts.devices')}</span>
          </div>
        </div>

        <div className="tpl-table-wrap">
          <table className="tpl-table clients-table" data-testid="clients-table">
            <thead>
              <tr>
                {sortHeader('name', t('clients.columns.client'))}
                {sortHeader('siren', t('clients.columns.siren'))}
                {sortHeader('deviceCount', t('clients.columns.devices'), true)}
                {list.showInactive && sortHeader('active', t('clients.columns.status'))}
                {sortHeader('lastActivity', t('clients.columns.lastActivity'))}
                <th className="tpl-col-open"><span className="sr-only">{t('clients.open')}</span></th>
              </tr>
            </thead>
            <tbody>
              {list.pageRows.map((client) => (
                <tr key={client.id} onClick={() => openClient(client)} className={`tpl-row client-row${client.active ? '' : ' is-off'}${client.depth ? ' is-agency-row' : ''}`} data-testid={`client-row-${client.id}`}>
                  <td className="tpl-cell-name">
                    <div className={`cl-name-line${client.depth ? ' is-agency' : ''}`}>
                      {client.depth === 1 && <span className="cl-tree-branch" aria-hidden="true">└</span>}
                      <span className="cl-avatar" style={{ ['--cl-hue' as any]: hueOf(client.id) }} aria-hidden="true">{clientInitials(client.name)}</span>
                      <div className="cl-name-text">
                        <strong className="tpl-name">
                          {client.name}
                          {isAgency(client) && <span className="cl-agency-badge" data-testid={`agency-badge-${client.id}`}>{t('clients.agencies.badge')}</span>}
                          {client.agencyCount > 0 && <span className="cl-agency-badge is-group">{t('clients.agencies.headOffice')}</span>}
                        </strong>
                        <span className="tpl-sub mono">{client.id}</span>
                      </div>
                      {client.agencyCount > 0 && (
                        <button
                          type="button"
                          className="cl-tree-toggle"
                          data-testid={`toggle-agencies-${client.id}`}
                          aria-expanded={!list.isCollapsed(client.id)}
                          onClick={(e) => { e.stopPropagation(); list.toggleCollapsed(client.id); }}
                        >
                          <span aria-hidden="true">{list.isCollapsed(client.id) ? '▸' : '▾'}</span> {t('clients.agencies.count', { count: client.agencyCount })}
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="cl-siren">
                    {client.siren
                      ? <code className="dt-code">{client.siren}</code>
                      : client.nic
                        ? <span className="tpl-muted">{t('clients.agencies.nic')} <code className="dt-code">{client.nic}</code></span>
                        : <span className="tpl-muted">–</span>}
                  </td>
                  <td className="numeric tpl-cell-count">
                    <span className="tpl-count">{formatNumber(client.agencyCount > 0 ? client.groupDeviceCount : client.deviceCount)}</span>
                    {client.agencyCount > 0 && client.deviceCount > 0 && <span className="tpl-sub cl-group-total is-warning">{t('clients.agencies.unassigned', { count: formatNumber(client.deviceCount) })}</span>}
                  </td>
                  {list.showInactive && (
                    <td>
                      <span className={`status-badge ${client.active ? 'active' : 'inactive'}`}>{client.active ? t('common.active') : t('common.inactive')}</span>
                    </td>
                  )}
                  <td className="tpl-muted">{client.lastActivity ? formatDate(client.lastActivity) : t('clients.detail.none')}</td>
                  <td className="tpl-col-open"><span className="tpl-chevron" aria-hidden="true">›</span></td>
                </tr>
              ))}
            </tbody>
          </table>

          {list.pageRows.length === 0 && (
            <ListEmpty title={t('clients.noClientsFound')} hint={list.hasFilters ? t('clients.emptyHint') : undefined} testId="clients-empty" />
          )}
        </div>

        <Pagination
          page={list.currentPage}
          totalPages={list.totalPages}
          totalItems={list.totalItems}
          pageSize={list.itemsPerPage}
          itemLabel="clients"
          onPageChange={list.setCurrentPage}
          onPageSizeChange={(size) => { list.setItemsPerPage(size); list.setCurrentPage(1); }}
        />
      </div>

      <ClientDetailModal
        client={selectedClient}
        isOpen={selectedClient !== null}
        onClose={() => setSelectedClient(null)}
        onDeviceSelect={() => {}}
        onClientUpdated={list.reload}
        siblings={list.allRows}
        onOpenClient={openClient}
        canAssignDevices={isAnepManager || isClientAdmin}
      />
    </div>
  );
};
